import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  rejectPendingIssueSuggestionsMutation,
  rejectPendingIssueSuggestionsMutation$data,
} from './__generated__/rejectPendingIssueSuggestionsMutation.graphql'

export type RejectSuggestionRef =
  | {kind: 'TYPE'; issueTypeId: string}
  | {kind: 'LABEL'; labelId: string}
  | {kind: 'FIELD'; issueFieldId: string}
  | {kind: 'CLOSE'}

export function commitRejectPendingIssueSuggestionsMutation({
  environment,
  issueId,
  actorId,
  suggestions,
  onError,
  onCompleted,
}: {
  environment: Environment
  issueId: string
  actorId: string
  suggestions: RejectSuggestionRef[]
  onError?: (error: Error) => void
  onCompleted?: (response: rejectPendingIssueSuggestionsMutation$data) => void
}) {
  return commitMutation<rejectPendingIssueSuggestionsMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation rejectPendingIssueSuggestionsMutation($input: RejectPendingIssueSuggestionsInput!) {
        rejectPendingIssueSuggestions(input: $input) {
          clientMutationId
        }
      }
    `,
    variables: {
      input: {
        issueId,
        actorId,
        suggestions: suggestions.map(s => {
          if (s.kind === 'TYPE') return {kind: 'TYPE', issueTypeId: s.issueTypeId}
          if (s.kind === 'LABEL') return {kind: 'LABEL', labelId: s.labelId}
          if (s.kind === 'FIELD') return {kind: 'FIELD', issueFieldId: s.issueFieldId}
          return {kind: 'CLOSE'}
        }),
      },
    },
    optimisticUpdater: store => markRejected(store, issueId, actorId, suggestions),
    updater: store => markRejected(store, issueId, actorId, suggestions),
    onError: error => onError?.(error),
    onCompleted: response => onCompleted?.(response),
  })
}

function markRejected(
  store: RecordSourceSelectorProxy,
  issueId: string,
  actorId: string,
  suggestions: RejectSuggestionRef[],
) {
  const issue = store.get(issueId)
  if (!issue) return
  const pendingSuggestions = issue.getLinkedRecords('pendingSuggestions')
  if (!pendingSuggestions) return

  for (const suggestion of suggestions) {
    const {typename, match} = matcherFor(suggestion, actorId)
    for (const s of pendingSuggestions) {
      if (!s) continue
      if (s.getValue('__typename') !== typename) continue
      const actor = s.getLinkedRecord('actor')
      if (actor?.getValue('id') !== actorId) continue
      if (!match(s)) continue
      // Relay stores fields under the schema name (`state`), not the fragment alias.
      s.setValue('REJECTED', 'state')
    }
  }
}

function matcherFor(suggestion: RejectSuggestionRef, _actorId: string) {
  if (suggestion.kind === 'TYPE') {
    return {
      typename: 'PendingTypeSuggestion',
      match: (s: ReturnType<RecordSourceSelectorProxy['get']>) =>
        s?.getLinkedRecord('issueType')?.getValue('id') === suggestion.issueTypeId,
    }
  }
  if (suggestion.kind === 'LABEL') {
    return {
      typename: 'PendingLabelSuggestion',
      match: (s: ReturnType<RecordSourceSelectorProxy['get']>) =>
        s?.getLinkedRecord('label')?.getValue('id') === suggestion.labelId,
    }
  }
  if (suggestion.kind === 'FIELD') {
    return {
      typename: 'PendingFieldSuggestion',
      match: (s: ReturnType<RecordSourceSelectorProxy['get']>) =>
        s?.getLinkedRecord('issueField')?.getValue('id') === suggestion.issueFieldId,
    }
  }
  // CLOSE: at most one pending close suggestion per actor, matched by actor alone.
  return {
    typename: 'PendingCloseSuggestion',
    match: () => true,
  }
}
